// Graph View Position Constants

// Single Collection View Positions
export const SINGLE_VIEW = {
  // Collection position (center)
  collection: {
    top: '50%',
    left: '50%'
  },

  // Item slot positions (4 corners around center)
  items: [
    { top: '20%', left: '10%' },   // Position 0: Top-left
    { top: '20%', right: '10%' },  // Position 1: Top-right
    { bottom: '20%', left: '10%' }, // Position 2: Bottom-left
    { bottom: '20%', right: '10%' } // Position 3: Bottom-right
  ]
};

// Dual Collection View Positions
export const DUAL_VIEW = {
  // Collection Y positions (as percentages for easy reuse in connection lines)
  topCollectionY: 32.5,
  bottomCollectionY: 67.5,

  // Collection positions (adjusted for even spacing from shared slots and regular slots)
  collections: [
    { top: '32.5%', left: '50%' },    // Index 0: Top collection
    { top: '67.5%', left: '50%' }     // Index 1: Bottom collection
  ],

  // Horizontal distribution for items (4 items per collection) - evenly distributed
  itemHorizontalPositions: ['12.5%', '37.5%', '62.5%', '87.5%'],

  // Top collection items (row above)
  topCollectionItems: {
    top: '15%'
  },

  // Bottom collection items (row below)
  bottomCollectionItems: {
    top: '85%'
  },

  // Shared slots (3 slots in the middle)
  sharedSlots: {
    top: '50%',
    horizontalPositions: ['25%', '50%', '75%']
  },

  // Grid layout (2x2) for items when in compact mode
  gridLayout: {
    // Collection Y positions for grid layout mode (slightly closer)
    topCollectionY: 35,
    bottomCollectionY: 65,

    // Collection positions
    collections: [
      { top: '35%', left: '50%' },
      { top: '65%', left: '50%' }
    ],

    // Top collection items
    topCollectionItems: {
      top: '17%'
    },

    // Bottom collection items
    bottomCollectionItems: {
      top: '83%'
    },

    // Horizontal positions for 2 columns
    // Farther row from collection (slightly wider spacing)
    horizontalPositionsFar: ['30%', '70%'],
    // Closer row to collection (wider spacing)
    horizontalPositionsClose: ['25%', '75%'],

    // Top collection grid (above collection)
    topCollectionGrid: {
      rows: ['10%', '22%']  // Row 0: far (10%), Row 1: close (22%)
    },

    // Bottom collection grid (below collection)
    bottomCollectionGrid: {
      rows: ['78%', '90%']  // Row 0: close (78%), Row 1: far (90%)
    },

    // Shared slots with wider spacing in grid layout
    sharedSlots: {
      horizontalPositions: ['15%', '50%', '85%']
    }
  },

  // Ultra-compact layout (triggered by height <= 920 or width <= 515)
  ultraCompact: {
    // Collection Y positions for ultra-compact mode
    topCollectionY: 38,
    bottomCollectionY: 62,

    // Collection positions
    collections: [
      { top: '38%', left: '50%' },
      { top: '62%', left: '50%' }
    ],

    // Top collection items (closer to collection)
    topCollectionItems: {
      top: '20%'
    },

    // Bottom collection items (closer to collection)
    bottomCollectionItems: {
      top: '80%'
    },

    // Grid layout for ultra-compact mode
    gridLayout: {
      topCollectionGrid: {
        rows: ['12%', '26%']
      },
      bottomCollectionGrid: {
        rows: ['74%', '88%']
      }
    }
  }
};

// Helper functions to get positions
export const getPositions = {
  // Single view: Get item position by slot index (0-3)
  singleViewItem: (position) => {
    return SINGLE_VIEW.items[position] || SINGLE_VIEW.items[0];
  },

  // Dual view: Get collection position by index (0 or 1)
  dualViewCollection: (index, isUltraCompact = false, isGridLayout = false) => {
    let collections;
    if (isUltraCompact) {
      collections = DUAL_VIEW.ultraCompact.collections;
    } else if (isGridLayout) {
      collections = DUAL_VIEW.gridLayout.collections;
    } else {
      collections = DUAL_VIEW.collections;
    }
    return collections[index] || collections[0];
  },

  // Dual view: Get item position by collection index and slot index
  dualViewItem: (collectionIndex, position, isUltraCompact = false, isGridLayout = false) => {
    const left = DUAL_VIEW.itemHorizontalPositions[position];
    let topItems, bottomItems;

    if (isUltraCompact) {
      topItems = DUAL_VIEW.ultraCompact.topCollectionItems;
      bottomItems = DUAL_VIEW.ultraCompact.bottomCollectionItems;
    } else if (isGridLayout) {
      topItems = DUAL_VIEW.gridLayout.topCollectionItems;
      bottomItems = DUAL_VIEW.gridLayout.bottomCollectionItems;
    } else {
      topItems = DUAL_VIEW.topCollectionItems;
      bottomItems = DUAL_VIEW.bottomCollectionItems;
    }

    if (collectionIndex === 0) {
      // Top collection - items in a row above it
      return {
        top: topItems.top,
        left
      };
    } else {
      // Bottom collection - items in a row below it
      return {
        top: bottomItems.top,
        left
      };
    }
  },

  // Dual view: Get shared slot position by index (0-2)
  dualViewSharedSlot: (position) => {
    return {
      top: DUAL_VIEW.sharedSlots.top,
      left: DUAL_VIEW.sharedSlots.horizontalPositions[position]
    };
  },

  // Dual view: Get shared slot position in grid layout by index (0-2)
  dualViewSharedSlotGrid: (position) => {
    return {
      top: DUAL_VIEW.sharedSlots.top,
      left: DUAL_VIEW.gridLayout.sharedSlots.horizontalPositions[position]
    };
  },

  // Dual view: Get item position in grid layout (2x2) by collection index and slot index
  dualViewItemGrid: (collectionIndex, position, isUltraCompact = false) => {
    // Calculate row and column from position (0-3)
    // Position 0: row 0, col 0
    // Position 1: row 0, col 1
    // Position 2: row 1, col 0
    // Position 3: row 1, col 1
    const row = Math.floor(position / 2);
    const col = position % 2;

    const gridLayout = isUltraCompact ? DUAL_VIEW.ultraCompact.gridLayout : DUAL_VIEW.gridLayout;

    if (collectionIndex === 0) {
      // Top collection - 2x2 grid above it
      // Row 0 is far, Row 1 is close
      const isFarRow = row === 0;
      const horizontalPositions = isFarRow
        ? DUAL_VIEW.gridLayout.horizontalPositionsFar
        : DUAL_VIEW.gridLayout.horizontalPositionsClose;
      const left = horizontalPositions[col];
      const top = gridLayout.topCollectionGrid.rows[row];
      return { top, left, invertCurve: isFarRow };
    } else {
      // Bottom collection - 2x2 grid below it
      // Row 0 is close, Row 1 is far
      const isFarRow = row === 1;
      const horizontalPositions = isFarRow
        ? DUAL_VIEW.gridLayout.horizontalPositionsFar
        : DUAL_VIEW.gridLayout.horizontalPositionsClose;
      const left = horizontalPositions[col];
      const top = gridLayout.bottomCollectionGrid.rows[row];
      return { top, left, invertCurve: isFarRow };
    }
  }
};
